import './assets/main.css';

import 'bootstrap';
import 'primeicons/primeicons.css';

 

import { createApp } from 'vue';
import { createPinia, setActivePinia } from 'pinia';
import { definePreset, palette } from '@primevue/themes';
import { registerModules } from "./register-modules";
import { createHead } from '@unhead/vue/client';

import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import PrimeVue from 'primevue/config';
import App from './App.vue';
import router from './router';
import programModule from './modules/program';
import hcetModule from './modules/hcet';
import hpModule from './modules/hp';
import foscModule from './modules/fosc';
import cexModule from './modules/cex';
import diModule from './modules/di';
import uroModule from './modules/uro';
import authStorePlugin from './plugins/authStore';
import keycloakService from '@services/keycloakService';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import InputMask from 'primevue/inputmask';
import DatePicker from 'primevue/datepicker'; 
import DataTable from 'primevue/datatable';
import Column from 'primevue/column'; 
import Card from 'primevue/card';
import Dialog from 'primevue/dialog';
import Message from 'primevue/message';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import ToggleButton from 'primevue/togglebutton';
import ConfirmDialog from 'primevue/confirmdialog';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import FocusTrap from 'primevue/focustrap';
import Ripple from 'primevue/ripple';
import ConfirmationService from 'primevue/confirmationservice';
import Aura from '@primevue/themes/aura';
import Badge from 'primevue/badge';

import ToastService from 'primevue/toastservice';
import convertNewLines from './utils/convertNewLines';
import logger from './utils/logger';
// import { Ripple } from 'primevue';
import Tooltip from 'primevue/tooltip';
import Quill from 'quill';


const head = createHead();

// QUILL EDITOR CUSTOMIZATION
const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'comic-sans', 'sans-serif', 'helvetica'];
Quill.register(Font, true);

const Size = Quill.import('formats/size');
Size.whitelist = ['12px', '14px', '16px', '20px'];
Quill.register(Size, true);

// Override the setContents method to apply default formatting
const originalSetContents = Quill.prototype.setContents;
Quill.prototype.setContents = function(delta, source) {
  const result = originalSetContents.call(this, delta, source);
  
  // Apply default formatting if content is empty or just a newline
  if (this.getLength() <= 1) {
    this.formatText(0, 1, {
      font: 'helvetica',
      size: '16px'
    });
  }
  
  return result;
};

// Also set defaults when text is inserted
const originalInsertText = Quill.prototype.insertText;
Quill.prototype.insertText = function(index, text, formats, source) {
  // Merge default formats with any provided formats
  const defaultFormats = {
    font: 'helvetica',
    size: '16px',
    ...formats
  };
  
  return originalInsertText.call(this, index, text, defaultFormats, source);
};

// Remove default tab behavior
const Keyboard = Quill.import('modules/keyboard');

delete Keyboard.DEFAULTS.bindings['tab'];
delete Keyboard.DEFAULTS.bindings['shift+tab'];

// APPLICATION PRESET AND CONFIGURATION
const appPreset = definePreset(Aura, {
  semantic: {
    primary: palette('#003865'),
    colorScheme: {
      light: {
        formField: {
           disabledBackground: '#f1f1f1',
        }
      }
    }
  }
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
setActivePinia(pinia);

const app =  createApp(App);
app.use(pinia);


app.config.errorHandler = (err) => {
    logger.error(err);
  };

app.directive('convert-newlines', convertNewLines);
app.directive('tooltip', Tooltip);
app.directive('focustrap', FocusTrap);

app.component('PvButton', Button);
app.component('PvInputText', InputText);
app.component('PvTabs', Tabs);
app.component('PvTab', Tab);
app.component('PvTabList', TabList);
app.component('PvTabPanels', TabPanels);
app.component('PvTabPanel', TabPanel);
app.component('PvSelect', Select);
app.component('PvInputMask', InputMask);
app.component('PvDatePicker', DatePicker);
app.component('PvDataTable', DataTable);
app.component('PvColumn', Column);
app.component('PvCard', Card);
app.component('PvDialog', Dialog);
app.component('PvMessage', Message);
app.component('PvToggleSwitch', ToggleSwitch);
app.component('PvToggleButton', ToggleButton);
app.component('PvConfirmDialog', ConfirmDialog);
app.component('PvTextarea', Textarea);
app.component('PvCheckbox', Checkbox);
app.component('PvInputNumber', InputNumber);
app.component('PvBadge', Badge);

registerModules({
    program: programModule,
    hcet: hcetModule,
    hp: hpModule,
    fosc: foscModule,
    di: diModule,
    cex: cexModule,
    uro: uroModule
});

app.use(PrimeVue, {
  ripple: true,
  theme: {
    preset: appPreset,
    options: {
      darkModeSelector: 'light-only',
      cssLayer: false
    } 
  }
});

app.directive('ripple', Ripple);
app.use(ToastService);
app.use(ConfirmationService);

app.use(head);

keycloakService.CallInit().then(async () => {
  app.use(authStorePlugin);
  app.use(router);
  app.mount('#app');
});
